import React from "react";
import { Msr, Drop, useOutside, popUp } from "./components";
import { birthYearOf } from "./cascade";
import { validateTeams, isTeamComp, usedHint, compareTeamComps, dancerAllowedInComp } from "./teamRules";
import "./t4.css";

// T4 — Step 2: Teams (team builder). Team CARDS with a searchable member
// pick-list — NOT drag-and-drop (screens.md §T4). Per card the cascade is
// competition -> dance -> figure (the figure list is a property of the CHOSEN
// DANCE, from the céilí reference — not of the competition). Composition is
// validated live by teamRules.js (the client mirror of the server engine);
// a failing team can proceed via the ATTESTATION override ("I have the
// regional director's permission"), captured with the team (acceptance C2).
// Paid (already registered) teams render as locked context cards — add-only.

let nextKey = 1;
export const newTeamKey = () => `t${Date.now()}_${nextKey++}`;

// Searchable roster popover behind the "+ Add dancer" slot (mockup 4-teams).
function MemberPicker({ roster, exclude, hintFor, onPick, gatedCount = 0 }) {
  const [open, setOpen] = React.useState(false);
  const [up, setUp] = React.useState(false);
  const [q, setQ] = React.useState("");
  const ref = useOutside(open, setOpen);
  const excluded = new Set(exclude.map(String));
  const list = roster.filter(
    (d) =>
      !excluded.has(String(d._id)) &&
      `${d.fname} ${d.lname}`.toLowerCase().includes(q.toLowerCase())
  );
  return (
    <div className={"pickwrap" + (open ? " open" : "")} ref={ref}>
      <button type="button" className="addslot"
        onClick={() => setOpen((o) => { if (!o) setUp(popUp(ref.current, 250)); return !o; })}>
        <Msr>person_add</Msr>Add dancer
      </button>
      {open && (
        <div className={"pick-pop" + (up ? " up" : "")}>
          <div className="srch">
            <Msr>search</Msr>
            <input autoFocus placeholder="Search roster…" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          {list.length === 0 && <div className="noopt">No dancers match.</div>}
          {/* Say WHY someone is missing — otherwise a teacher hunting for a
              dancer who isn't listed reads it as the roster being broken. */}
          {gatedCount > 0 && (
            <div className="noopt gated">
              {gatedCount} dancer{gatedCount > 1 ? "s" : ""} not eligible for this competition
            </div>
          )}
          {list.map((d) => {
            const hint = hintFor(d._id);
            const yr = birthYearOf(d.birthday);
            return (
              <div key={String(d._id)} className="pick-opt"
                onClick={() => { onPick(d); setOpen(false); setQ(""); }}>
                {d.fname} {d.lname}
                {hint ? <span className="used">{hint}</span> : yr && <span className="ag">{yr}</span>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function TeamCard({ team, errors, teams, savedTeams, roster, compsById, teamComps, dancesByKey, onChange, onDelete, onOverride }) {
  const comp = team.competition_id ? compsById[String(team.competition_id)] : null;
  const size = comp && comp.teamConfig ? comp.teamConfig.size : null;
  const danceOptions = (comp && comp.teamConfig && comp.teamConfig.danceOptions) || [];
  const dance = team.danceKey ? dancesByKey[team.danceKey] : null;
  const figures = (dance && dance.figures) || [];
  const memberIds = team.members || [];
  // fixed slot grid for sized comps; figure/drama grow one open slot at a time
  const slotCount = size || memberIds.length + 1;
  const slots = Array.from({ length: slotCount }, (_, i) => memberIds[i] || null);
  const rosterById = Object.fromEntries(roster.map((d) => [String(d._id), d]));
  // The picker offers only dancers who could NEVER be made valid by a regional
  // director's permission (see teamRules.dancerAllowedInComp) — reviewable
  // breaches stay pickable so the teacher can select and override.
  // Dancers already on ANOTHER team in THIS comp (cart or registered) are
  // also hard-filtered — one team per competition per dancer (Devin
  // 2026-08-20). A saved team identical to this card is the same team being
  // resubmitted and doesn't lock out its own dancers.
  const takenInComp = React.useMemo(() => {
    if (!comp) return new Set();
    const mine = new Set((team.members || []).map(String));
    const taken = new Set();
    const consider = (t, isSaved) => {
      if (String(t.competition_id) !== String(comp._id)) return;
      const ids = (t.members || []).map((m) => String(m && m.dancer_id ? m.dancer_id : m));
      if (isSaved && ids.length === mine.size && ids.every((id) => mine.has(id))) return;
      ids.forEach((id) => taken.add(id));
    };
    teams.forEach((t) => { if (t.key !== team.key) consider(t, false); });
    (savedTeams || []).forEach((t) => consider(t, true));
    return taken;
  }, [comp, teams, savedTeams, team]);
  const eligibleRoster = React.useMemo(
    () =>
      comp
        ? roster.filter((d) => dancerAllowedInComp(d, comp) && !takenInComp.has(String(d._id)))
        : roster,
    [roster, comp, takenInComp]
  );
  const gatedCount = roster.length - eligibleRoster.length;

  const hard = errors.filter((e) => !e.overridable);
  const violations = errors.filter((e) => e.overridable);
  const covered = (e) =>
    team.override && team.override.overridden &&
    (team.override.overriddenRules || []).includes(e.code);
  const uncovered = violations.filter((e) => !covered(e));
  const overridden = team.override && team.override.overridden && violations.length > 0 && uncovered.length === 0;

  const setMembers = (members) => onChange({ ...team, members });
  const hintFor = (dancerId) => usedHint(dancerId, team, teams, savedTeams, compsById);

  return (
    <div className={"tcard" + (hard.length || uncovered.length ? " flagged" : "")}>
      <div className="tcard-h">
        <div className="row1">
          <div className="fld" style={{ flex: 1 }}>
            <label>Competition</label>
            <Drop
              value={team.competition_id ? String(team.competition_id) : ""}
              placeholder="Choose a team competition"
              width="100%"
              options={teamComps.map((c) => ({
                value: String(c._id),
                label: `${c.name || c.code} (${c.code})`,
              }))}
              onChange={(v) => {
                const next = compsById[v];
                const opts = (next && next.teamConfig && next.teamConfig.danceOptions) || [];
                // keep dance/figure only if still allowed under the new comp;
                // a single-dance comp (4-Hand is Reel-only, Rule 17a) has no
                // choice to make — assign it (figures stay choosable, they
                // belong to the dance).
                const danceKey = opts.includes(team.danceKey)
                  ? team.danceKey
                  : opts.length === 1
                    ? opts[0]
                    : "";
                const nsize = next && next.teamConfig ? next.teamConfig.size : null;
                const members = nsize ? memberIds.slice(0, nsize) : memberIds;
                onChange({ ...team, competition_id: v, danceKey, figure: danceKey ? team.figure : "", members });
              }}
            />
          </div>
          <button type="button" className="delbtn" title="Delete team" onClick={onDelete}><Msr>delete</Msr></button>
        </div>
        {comp && danceOptions.length > 0 && (
          <div className="selrow">
            <div className="fld">
              <label>Dance</label>
              <Drop
                value={team.danceKey || ""} placeholder="Choose dance" width="100%"
                options={danceOptions.map((k) => ({ value: k, label: (dancesByKey[k] && dancesByKey[k].name) || k }))}
                onChange={(k) => {
                  const nf = ((dancesByKey[k] && dancesByKey[k].figures) || []).includes(team.figure) ? team.figure : "";
                  onChange({ ...team, danceKey: k, figure: nf });
                }}
              />
            </div>
            {figures.length > 0 && (
              <div className="fld">
                <label>Figure</label>
                <Drop
                  value={team.figure || ""} placeholder="Choose figure" width="100%"
                  options={figures.map((f) => ({ value: f, label: f }))}
                  onChange={(f) => onChange({ ...team, figure: f })}
                />
              </div>
            )}
          </div>
        )}
      </div>

      {comp && (
        <>
          <div className="slots-h">Members · {memberIds.length}/{size || memberIds.length}</div>
          <div className={"slots" + ((size || 4) > 4 ? " wide" : "")}>
            {slots.map((id, i) => {
              const d = id ? rosterById[String(id)] : null;
              const hint = id ? hintFor(id) : null;
              return (
                <div className="slot" key={i}>
                  {id ? (
                    <div className="member">
                      <span className="idx">{i + 1}</span>
                      <div style={{ minWidth: 0 }}>
                        <div className="nm">
                          <span>{d ? `${d.fname} ${d.lname}` : "Dancer"}</span>
                          {d && birthYearOf(d.birthday) && <span className="ag">{birthYearOf(d.birthday)}</span>}
                        </div>
                        {hint && <div className="used"><Msr>info</Msr>{hint}</div>}
                      </div>
                      <button type="button" className="rm" title="Remove"
                        onClick={() => setMembers(memberIds.filter((m, j) => j !== i))}>
                        <Msr>close</Msr>
                      </button>
                    </div>
                  ) : (
                    <MemberPicker roster={eligibleRoster} exclude={memberIds} hintFor={hintFor}
                      gatedCount={gatedCount}
                      onPick={(d) => setMembers([...memberIds, String(d._id)])} />
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {overridden ? (
        <div className="status overridden">
          <Msr>verified_user</Msr>
          <span className="txt">Override on file — regional director permission attested.</span>
        </div>
      ) : errors.length === 0 && comp ? (
        <div className="status ok"><Msr>check_circle</Msr><span className="txt">Team valid</span></div>
      ) : errors.length > 0 ? (
        <div className="status bad">
          <Msr>flag</Msr>
          <span className="txt">
            {[...hard, ...uncovered].map((e) => e.message).join(" ") ||
              violations.map((e) => e.message).join(" ")}
          </span>
          {uncovered.length > 0 && hard.length === 0 && (
            <button type="button" className="ovr" onClick={() => onOverride(uncovered.map((e) => e.code))}>Override</button>
          )}
        </div>
      ) : null}
    </div>
  );
}

// A registered (paid) team — locked context card (add-only, T9).
function PaidTeamCard({ team }) {
  const comp = team.competition || {};
  return (
    <div className="tcard paid">
      <div className="tcard-h">
        <div className="row1">
          <div className="paid-title">
            <span className="nm">{comp.name || comp.code}{team.teamLetter ? ` · Team ${team.teamLetter}` : ""}</span>
            {team.danceName && <span className="dn">{team.danceName}{team.figure ? ` · ${team.figure}` : ""}</span>}
          </div>
          <span className="paidtag"><Msr>check_circle</Msr>Registered</span>
        </div>
      </div>
      <div className="slots-h">Members · {(team.members || []).length}</div>
      <div className="paid-members">
        {(team.members || []).map((m, i) => (
          <span className="chip" key={i}>{m.fname} {m.lname}</span>
        ))}
      </div>
      {team.overridden && (
        <div className="status overridden">
          <Msr>verified_user</Msr>
          <span className="txt">Override on file — regional director permission attested.</span>
        </div>
      )}
    </div>
  );
}

export default function Teams({ event, roster, competitions, reference, teams, savedTeams, onChangeTeams }) {
  const [attestFor, setAttestFor] = React.useState(null); // { key, codes }
  const [attChecked, setAttChecked] = React.useState(false);

  // Sorted for display — the catalog arrives in insertion order (see
  // teamRules.compareTeamComps); every consumer below reads this one list.
  const teamComps = React.useMemo(
    () => competitions.filter(isTeamComp).sort(compareTeamComps),
    [competitions]
  );
  const compsById = React.useMemo(
    () => Object.fromEntries(competitions.map((c) => [String(c._id), c])),
    [competitions]
  );
  const dancesByKey = React.useMemo(
    () => Object.fromEntries((reference.ceiliDances || []).map((d) => [d.key, d])),
    [reference]
  );
  const dancersById = React.useMemo(
    () => Object.fromEntries(roster.map((d) => [String(d._id), d])),
    [roster]
  );
  const visibleRoster = React.useMemo(() => roster.filter((d) => !d.hidden), [roster]);

  // live validation across ALL cards (cross-team rules need the full set)
  const validation = React.useMemo(
    () =>
      validateTeams({
        teams,
        competitionsById: compsById,
        dancersById,
        ceiliDancesByKey: dancesByKey,
        savedTeams: (savedTeams || []).map((t) => ({
          _id: t._id,
          competition_id: t.competition && t.competition._id,
          members: t.members || [],
        })),
        teamRules: event && event.teamRules,
      }),
    [teams, compsById, dancersById, dancesByKey, savedTeams, event]
  );
  const errorsFor = (team, i) =>
    validation.errors.filter((e) => String(e.teamKey) === String(team.key || `team-${i}`));

  const patch = (key, next) => onChangeTeams(teams.map((t) => (t.key === key ? next : t)));
  const remove = (key) => onChangeTeams(teams.filter((t) => t.key !== key));
  const add = () =>
    onChangeTeams([
      ...teams,
      { key: newTeamKey(), competition_id: null, danceKey: "", figure: "", members: [], override: { overridden: false, overriddenRules: [] } },
    ]);

  const confirmOverride = () => {
    const t = teams.find((x) => x.key === attestFor.key);
    if (t) {
      const prior = (t.override && t.override.overriddenRules) || [];
      patch(t.key, {
        ...t,
        override: { overridden: true, overriddenRules: [...new Set([...prior, ...attestFor.codes])] },
      });
    }
    setAttestFor(null);
    setAttChecked(false);
  };

  return (
    <div className="t4">
      <div className="teams-top">
        <div className="lead">
          Build céilí and figure teams from your school's dancers. Choosing a competition sets the number of member slots.
        </div>
      </div>

      {teamComps.length === 0 && (
        <div className="helper"><Msr>info</Msr>This event has no team competitions.</div>
      )}

      <div className="tgrid">
        {(savedTeams || []).map((t) => <PaidTeamCard key={String(t._id)} team={t} />)}
        {teams.map((t, i) => (
          <TeamCard
            key={t.key} team={t} errors={errorsFor(t, i)}
            teams={teams} savedTeams={(savedTeams || []).map((s) => ({
              _id: s._id, competition_id: s.competition && s.competition._id, members: s.members || [],
            }))}
            roster={visibleRoster} compsById={compsById} teamComps={teamComps} dancesByKey={dancesByKey}
            onChange={(next) => patch(t.key, next)}
            onDelete={() => remove(t.key)}
            onOverride={(codes) => { setAttestFor({ key: t.key, codes }); setAttChecked(false); }}
          />
        ))}
        {teamComps.length > 0 && (
          <button type="button" className="addteam" onClick={add}>
            <Msr>add_circle</Msr>Add a team
          </button>
        )}
      </div>

      {attestFor && (
        <div className="modal-scrim" onClick={() => setAttestFor(null)}>
          <div className="omodal" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Override team validation">
            <div className="modal-ic"><Msr>gavel</Msr></div>
            <h3>Override team validation</h3>
            <p>
              This team doesn't meet one or more standard rules. You can submit it anyway
              with the regional director's permission on record.
            </p>
            <div className={"att" + (attChecked ? " on" : "")} onClick={() => setAttChecked((v) => !v)}>
              <span className="box">{attChecked && <Msr>check</Msr>}</span>
              <span className="at-tx">I confirm I have the regional director's permission for this team.</span>
            </div>
            <div className="modal-act">
              <button type="button" className="mbtn ghost" onClick={() => setAttestFor(null)}>Cancel</button>
              <button type="button" className="mbtn primary" disabled={!attChecked} onClick={confirmOverride}>
                Confirm override
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
